import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellProps, Column, Renderer } from 'react-table';
import { Flex, Icon, TableData, Text } from '@wegroup/design-system';
import { Coverage } from '../../../types/AdvisoryReport';
import TableRowActions from '../components/shared/TableRowActions';
import { toEuro } from '../../../../../sales-conversation/src/utils/stringUtils';
import useAddInsuranceModalContext from '../components/insurance-policies/hooks/useAddInsuranceModalContext';
import useAdvisoryReportCurrency from './useAdvisoryReportCurrency';

export type CellRenderer = Renderer<CellProps<Coverage & TableData>>;

interface ReturnProps {
  columns: Column<Coverage & TableData>[];
}

interface Props {
  onEdit: (id: string) => void;
  onRemove: (id: string) => void;
  /** coverage id which is currently open for editing */
  coverageToEdit?: string;
}

const useInsuranceModalCoverageTable = ({
  onEdit,
  onRemove,
  coverageToEdit,
}: Props): ReturnProps => {
  const { t } = useTranslation();
  const { isCoverageEditOpen } = useAddInsuranceModalContext();
  const { currency } = useAdvisoryReportCurrency();

  const renderCoverageCell: CellRenderer = ({ cell }) => {
    return <Text color="black">{cell.value || '-'}</Text>;
  };
  const renderDefaultCell: CellRenderer = ({ cell }) => {
    return <p>{cell.value || '-'}</p>;
  };

  const renderCurrencyCell: CellRenderer = ({ cell }) => {
    try {
      const formattedValue = cell?.value ? toEuro(cell.value, currency) : '-';
      return <p>{formattedValue}</p>;
    } catch (error) {
      console.error('Error in renderCurrencyCell:', error);
      return <p>-</p>;
    }
  };

  const renderActionCell: CellRenderer = ({ row }) => {
    const coverageId = row.original.id;
    if (coverageId === coverageToEdit) {
      return (
        <Flex justify="flex-end" w="100%">
          <Icon name="GeneralActionsEyeClosed" color="primary.500" />
        </Flex>
      );
    }

    return (
      <TableRowActions
        onEdit={() => onEdit(row.original.id)}
        onRemove={() => onRemove(row.original.id)}
        isEditDisabled={isCoverageEditOpen}
        isRemoveDisabled={isCoverageEditOpen}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: t('ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_COVERAGE'),
        accessor: 'name',
        disableSortBy: true,
        isSticky: true,
        Cell: renderCoverageCell,
      },
      {
        Header: t(
          'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_INSURED-VALUE',
        ),
        accessor: 'insured_value',
        disableSortBy: true,
        Cell: renderCurrencyCell,
      },
      {
        Header: t('ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DETAIL_OWN-RISK'),
        accessor: 'deductible',
        disableSortBy: true,
        isSticky: true,
        Cell: renderDefaultCell,
      },
      {
        Header: t('PREMIUM'),
        accessor: 'premium',
        disableSortBy: true,
        isSticky: true,
        Cell: renderCurrencyCell,
      },
      {
        Header: (
          <Text align="right" w="100%">
            {t('ACTIONS')}
          </Text>
        ),
        accessor: 'actions',
        disableSortBy: true,
        isSticky: true,
        Cell: renderActionCell,
      },
    ],
    [coverageToEdit, isCoverageEditOpen],
  );
  return {
    columns,
  };
};

export default useInsuranceModalCoverageTable;
